import { memo as Memo, useCallback, useState, useEffect, useRef, useMemo } from 'react'

//* Components
import Container from 'components/common/Container'
import Text from 'components/common/Text'
import Image from 'components/common/Image'
import Icon from 'components/common/Icon'

//* Styles
import MusicStyle from './style'

const Music = Memo(({ title, subtitle, musicList, first }) => {
	//! Refs
	const canvasRef = useRef()
	const audioRef = useRef()
	const audioContext = useRef()
	const audioSrc = useRef()
	const analyser = useRef()
	const animFrame = useRef()
	const dataArray = useRef()

	//! States
	const [active, setActive] = useState(0)
	const [isPlaying, setIsPlaying] = useState(false)
	const [prevBtnClicked, setPrevBtnClicked] = useState(false)
	const [playBtnClicked, setPlayBtnClicked] = useState(false)
	const [nextBtnClicked, setNextBtnClicked] = useState(false)
	const [loading, setLoading] = useState(false)

	//! Has Music
	const hasMusic = useMemo(() => musicList && musicList.length, [musicList])

	//! Handle prev click
	const handlePrevClick = useCallback(() => {
		active !== 0 && setActive(active - 1)
	}, [active, isPlaying])

	//! Handle next click
	const handleNextClick = useCallback(() => {
		active !== musicList.length - 1 && setActive(active + 1)
	}, [active, isPlaying, musicList])

	//! Handle play/pause click
	const handlePlayClick = useCallback(async () => {
		if (isPlaying) {
			setIsPlaying(false)
			audioRef.current.pause()
		} else {
			setIsPlaying(true)
			await play()
		}
	}, [active, isPlaying])

	const play = async () => {
		setLoading(true)
		await audioRef.current.play().then((_) => {
			setLoading(false)
			audioRef.current.play()
		})
	}

	//!Animation
	useEffect(() => {
		animFrame.current = requestAnimationFrame(animate)

		return () => {
			cancelAnimationFrame(animFrame.current)
		}
	}, [])

	const animate = () => {
		animFrame.current = requestAnimationFrame(animate)

		if (analyser.current) {
			const bufferLength = analyser.current.frequencyBinCount
			dataArray.current = new Uint8Array(bufferLength)
			analyser.current.getByteFrequencyData(dataArray.current)
		}

		const canvas = canvasRef.current
		const canvasObj = canvas.getBoundingClientRect()
		const dpr = window.devicePixelRatio || 1
		canvas.width = canvasObj.width * dpr
		canvas.height = canvasObj.height * dpr
		const ctx = canvas.getContext('2d')
		ctx.scale(dpr, dpr)
		ctx.translate(0, canvas.offsetHeight / 2)
		ctx.lineWidth = 7
		ctx.strokeStyle = '#fff'

		if (dataArray.current) {
			const f = 0.5
			const t = 0.3
			const size = 20
			const partialSize = canvas.offsetWidth / size
			let dx1 = 0
			let dy1 = 0
			let dx2 = 0
			let dy2 = 0
			let preP = { x: -20, y: 0 }
			let curP, nexP, m
			const pr = window.innerWidth / 1920

			ctx.beginPath()
			ctx.moveTo(0, 0)

			for (var i = 1; i < size; i++) {
				const newY = dataArray.current[i] * pr
				const newNextY = dataArray.current[i + 1] * pr
				curP = { x: partialSize * [i], y: i % 2 ? newY : -newY }
				nexP = { x: partialSize * [i + 1], y: newNextY }

				if (nexP) {
					m = (nexP.y - preP.y) / (nexP.x - preP.x)
					dx2 = (nexP.x - curP.x) * -f
					dy2 = dx2 * m * t
				} else {
					dx2 = 0
					dy2 = 0
				}
				ctx.bezierCurveTo(preP.x - dx1, preP.y - dy1, curP.x + dx2, curP.y + dy2, curP.x, curP.y)
				dx1 = dx2
				dy1 = dy2
				preP = curP
			}

			dx2 = (canvas.offsetWidth + 20 - nexP.x) * -f
			dy2 = dx2 * m * t
			ctx.bezierCurveTo(preP.x - dx1, preP.y - dy1, canvas.offsetWidth + dx2, 0, canvas.offsetWidth + 20, 0)
			ctx.stroke()
		} else {
			ctx.beginPath()
			ctx.moveTo(0, 0)
			ctx.lineTo(canvas.offsetWidth, 0)
			ctx.stroke()
		}
	}

	//! Handle active state change
	useEffect(() => {
		if (audioRef.current) {
			if (!audioSrc.current && isPlaying) {
				audioContext.current = new (window.AudioContext || window.webkitAudioContext)()
				audioSrc.current = audioContext.current.createMediaElementSource(audioRef.current)
				analyser.current = audioContext.current.createAnalyser()
				analyser.current.fftSize = 64
				audioSrc.current.connect(analyser.current)
				analyser.current.connect(audioContext.current.destination)
				audioContext.current.resume()

				const bufferLength = analyser.current.frequencyBinCount
				dataArray.current = new Uint8Array(bufferLength)
			}

			if (audioSrc.current) {
				audioSrc.current.crossOrigin = 'anonymous'
				audioRef.current.crossOrigin = 'anonymous'
			}

			isPlaying && play()

			audioRef.current?.addEventListener('ended', onMusicEnd)
			audioRef.current?.addEventListener('pause', onMusicPaused)
			audioRef.current?.addEventListener('play', onMusicPlayed)

			return () => {
				audioRef.current?.removeEventListener('ended', onMusicEnd)
				audioRef.current?.removeEventListener('pause', onMusicPaused)
				audioRef.current?.removeEventListener('play', onMusicPlayed)
			}
		}
	}, [active, isPlaying])

	//! Handle music end
	const onMusicEnd = useCallback(() => {
		const nextIndex = active + 1 < musicList.length ? active + 1 : 0
		setActive(nextIndex)
		setIsPlaying(true)
	}, [active, musicList])

	//! Handle music paused
	const onMusicPaused = useCallback(() => {
		isPlaying && setIsPlaying(false)
	}, [isPlaying])

	//! Handle music played
	const onMusicPlayed = useCallback(() => {
		!isPlaying && setIsPlaying(true)
	}, [isPlaying])

	const audioHTML = useMemo(
		() => (hasMusic ? <audio crossOrigin='anonymous' ref={audioRef} src={musicList[active].music.url} /> : null),
		[active, musicList, hasMusic]
	)

	return (
		<Container full section first={first}>
			<MusicStyle className={`primaryColor3 bgCobalt `}>
				<Text tag='h2' text={title} className='h2 font-soundscape music-title' />

				<Text tag='h3' text={subtitle} className='h6 font-ping-medium music-subtitle' />

				<div className='music-middle-info-cont'>
					<span className='music-middle-info-bg' />
					{hasMusic ? (
						<>
							<Image objectFit='cover' className='music-image' src={musicList[active].image.url} alt={musicList[active].image.alt} />

							<Text className='music-item-artist font-ping-medium h6' text={musicList[active].artist} />

							<Text className=' music-item-title font-soundscape h5' text={musicList[active].musicTitle} />
						</>
					) : null}

					<canvas className='equalizer' ref={canvasRef} />
				</div>

				{hasMusic ? (
					<div className='music-action-buttons-cont'>
						<span
							className={`music-btn ${active === 0 ? 'disabled' : ''} ${
								prevBtnClicked ? 'normal' : ''
							} music-icon-prev bgCobalt-text primaryColor3-bg`}
							onClick={handlePrevClick}
							onMouseDown={() => setPrevBtnClicked(true)}
							onMouseUp={() => setPrevBtnClicked(false)}
						>
							<Icon name='next' />
						</span>
						<span
							className={`music-btn ${playBtnClicked ? 'normal' : ''} music-icon-play bgCobalt-text primaryColor3-bg`}
							onClick={handlePlayClick}
							onMouseDown={() => setPlayBtnClicked(true)}
							onMouseUp={() => setPlayBtnClicked(false)}
						>
							<Icon name={`${isPlaying ? 'pause' : 'play'}`} />
						</span>
						<span
							className={`music-btn  ${active === musicList.length - 1 ? 'disabled' : ''}  ${
								nextBtnClicked ? 'normal' : ''
							}  music-icon-next bgCobalt-text primaryColor3-bg`}
							onClick={handleNextClick}
							onMouseDown={() => setNextBtnClicked(true)}
							onMouseUp={() => setNextBtnClicked(false)}
						>
							<Icon name='next' />
						</span>
					</div>
				) : null}

				{audioHTML}
			</MusicStyle>
		</Container>
	)
})

export default Music
